import React, { useState, useRef, useEffect } from "react";
import { Card, Col, Form, Nav, ProgressBar, Row, Tab, Button, Image } from "react-bootstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import Swal from "sweetalert2";
import logoDark from "assets/images/brands/logo-gram.png"; // Import the logo

const FormQualification = () => {
    const [activeTab, setActiveTab] = useState<number>(0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [capturedImage, setCapturedImage] = useState<string | null>(null); // State to store the captured image
    const [jabatanOptions, setJabatanOptions] = useState<Array<{ id: number; jabatan: string }>>([]);
    // Fetch jabatan from API
    useEffect(() => {
        const fetchJabatan = async () => {
            try {
                const response = await (await import('axios')).default.get('/master-jabatan');
                if (Array.isArray(response.data.data)) {
                    setJabatanOptions(response.data.data);
                } else {
                    setJabatanOptions([]);
                }
            } catch (error) {
                setJabatanOptions([]);
            }
        };
        fetchJabatan();
    }, []);

    // Handler for KTP upload and OCR
    const handleKtpUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.currentTarget.files?.[0];
        if (!file) return;
        validation.setFieldValue("ktp", file);
        try {
            const formData = new FormData();
            formData.append("image", file);
            const axios = (await import("axios")).default;
            const response = await axios.post("/ocr/parse-image", formData, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            // Handle OCR response here (e.g., autofill form, show result, etc)
            // Example: Swal.fire({ icon: 'success', title: 'OCR Success', text: JSON.stringify(response.data) });
        } catch (error) {
            // Optionally show error
            // Swal.fire({ icon: 'error', title: 'OCR Failed', text: error instanceof Error ? error.message : 'Unknown error' });
        }
    };

    // Refs for form fields
    const fieldRefs = useRef<{ [key: string]: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement | null }>({});

    const validation = useFormik({
        initialValues: {
            jabatan: "",
            email: "",
            whatsapp: "",
            nik: "",
            nama: "",
            tanggal_lahir: "",
            tempat_lahir: "",
            jenis_kelamin: "",
            golongan_darah: "",
            alamat: "",
            alamat_domisili: "",
            agama: "",
            status_perkawinan: "",
            jumlah_anak: "",
            emergency_contact_relation: "",
            emergency_contact_name: "",
            emergency_contact_number: "",
            pendidikan_terakhir: "",
            jurusan: "",
            bahasa: "",
            kendaraan: "",
            sim: [],
            ktp: null,
            upload_ijazah: null,
            foto_body: null,
            foto_cv: null,
            foto_kk: null,
            foto_skck: null,
            foto_npwp: null,
        },
        validationSchema: Yup.object({
            jabatan: Yup.string().required("Please select a position"),
            email: Yup.string().email("Invalid email").required("Email is required"),
            whatsapp: Yup.string().required("WhatsApp number is required"),
            nik: Yup.string().required("NIK is required"),
            nama: Yup.string().required("Full name is required"),
            tanggal_lahir: Yup.string().required("Date of birth is required"),
            tempat_lahir: Yup.string().required("Place of birth is required"),
            jenis_kelamin: Yup.string().required("Gender is required"),
            golongan_darah: Yup.string().required("Blood type is required"),
            alamat: Yup.string().required("KTP address is required"),
            alamat_domisili: Yup.string().required("Current address is required"),
            agama: Yup.string().required("Religion is required"),
            status_perkawinan: Yup.string().required("Marital status is required"),
            jumlah_anak: Yup.string().required("Number of children is required"),
            emergency_contact_relation: Yup.string().required("Emergency contact relation is required"),
            emergency_contact_name: Yup.string().required("Emergency contact name is required"),
            emergency_contact_number: Yup.string().required("Emergency contact number is required"),
            pendidikan_terakhir: Yup.string().required("Last education is required"),
            jurusan: Yup.string().required("Major is required"),
            bahasa: Yup.string().required("English proficiency is required"),
            kendaraan: Yup.string().required("Vehicle is required"),
            sim: Yup.array().required("SIM is required"),
            ktp: Yup.mixed().required("KTP photo is required"),
            upload_ijazah: Yup.mixed().required("Ijazah photo is required"),
            foto_body: Yup.mixed().required("Recent photo is required"),
            foto_cv: Yup.mixed().required("CV photo is required"),
            foto_kk: Yup.mixed().required("Family card photo is required"),
            foto_skck: Yup.mixed().required("SKCK photo is required"),
            foto_npwp: Yup.mixed().required("NPWP photo is required"),
        }),
        onSubmit: async (values) => {
            setIsSubmitting(true);
            try {
                // Simulate API call
                await new Promise((resolve) => setTimeout(resolve, 1000));
                Swal.fire({
                    icon: "success",
                    title: "Form Submitted Successfully",
                    showConfirmButton: false,
                    timer: 1000,
                });
            } catch (error) {
                Swal.fire({
                    icon: "error",
                    title: "An error occurred",
                    text: "Please try again later.",
                });
            } finally {
                setIsSubmitting(false);
            }
        },
    });

    const handleNext = async () => {
        const fieldsToValidate = [];
        if (activeTab === 0) {
            fieldsToValidate.push("jabatan");
        } else if (activeTab === 1) {
            fieldsToValidate.push(
                "email",
                "whatsapp",
                "nik",
                "nama",
                "tanggal_lahir",
                "tempat_lahir",
                "jenis_kelamin",
                "golongan_darah",
                "alamat",
                "alamat_domisili",
                "agama",
                "status_perkawinan",
                "jumlah_anak"
            );
        } else if (activeTab === 2) {
            fieldsToValidate.push(
                "emergency_contact_relation",
                "emergency_contact_name",
                "emergency_contact_number"
            );
        } else if (activeTab === 3) {
            fieldsToValidate.push(
                "ktp",
                "upload_ijazah",
                "foto_body",
                "foto_cv",
                "foto_kk",
                "foto_skck",
                "foto_npwp"
            );
        }

        await validation.validateForm();
        const errors = validation.errors;
        const firstErrorField = fieldsToValidate.find((field) => errors[field as keyof typeof errors]);

        if (!firstErrorField) {
            setActiveTab(activeTab + 1);
        } else {
            // Focus on the first field with an error
            if (fieldRefs.current[firstErrorField]) {
                fieldRefs.current[firstErrorField]?.focus();
            }
            Swal.fire({
                icon: "error",
                title: "Validation Error",
                text: "Please fill in all mandatory fields before proceeding.",
            });
        }
    };

    const handleBack = () => {
        setActiveTab(activeTab - 1);
    };

    const openCameraWithTemplate = async () => {
        try {
            // Check if mediaDevices and getUserMedia are supported
            if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
                alert(
                    "Your browser does not support camera access. Please ensure you are using Chrome or Safari and the site is served over HTTPS."
                );
                return;
            }

            // Open the rear camera
            const stream = await navigator.mediaDevices.getUserMedia({
                video: { facingMode: "environment" }, // Use the rear camera
            });
            const videoElement = document.createElement("video");
            videoElement.srcObject = stream;
            videoElement.play();

            // Show a modal with the video stream and a capture button
            Swal.fire({
                title: '<h4 style="color: #495057; font-weight: bold;">Capture KTP</h4>',
                html: `
                    <div style="position: relative; width: 100%; height: 300px; background: #000; border-radius: 12px; overflow: hidden; box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);">
                        <video id="cameraStream" autoplay playsinline style="width: 100%; height: 100%; object-fit: cover;"></video>
                        <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); width: 80%; height: 50%; border: 3px dashed #007bff; border-radius: 8px; box-shadow: 0 0 15px rgba(0, 123, 255, 0.5);"></div>
                        <p style="position: absolute; bottom: 10px; left: 50%; transform: translateX(-50%); color: #fff; font-size: 14px; text-shadow: 0 0 5px rgba(0, 0, 0, 0.7);">
                            Align your KTP within the frame
                        </p>
                    </div>
                    <div style="margin-top: 20px; display: flex; justify-content: center; gap: 10px;">
                        <button id="captureButton" style="padding: 10px 20px; background-color: #007bff; color: #fff; border: none; border-radius: 5px; cursor: pointer; font-size: 14px; font-weight: bold; display: flex; align-items: center; gap: 8px;">
                            <i class="ri-camera-line"></i> Capture
                        </button>
                        <button id="cancelButton" style="padding: 10px 20px; background-color: #6c757d; color: #fff; border: none; border-radius: 5px; cursor: pointer; font-size: 14px; font-weight: bold;">
                            Cancel
                        </button>
                    </div>
                `,
                showConfirmButton: false,
                didOpen: () => {
                    const video = document.getElementById("cameraStream") as HTMLVideoElement;
                    video.srcObject = stream;

                    const captureButton = document.getElementById("captureButton");
                    const cancelButton = document.getElementById("cancelButton");

                    captureButton?.addEventListener("click", () => {
                        const canvas = document.createElement("canvas");
                        canvas.width = video.videoWidth;
                        canvas.height = video.videoHeight;
                        const context = canvas.getContext("2d");
                        if (context) {
                            context.drawImage(video, 0, 0, canvas.width, canvas.height);
                            const imageData = canvas.toDataURL("image/png");
                            setCapturedImage(imageData); // Save the image data to the state
                            validation.setFieldValue("ktp", imageData); // Save the image data to the form field
                            Swal.close(); // Close the modal
                        }
                    });

                    cancelButton?.addEventListener("click", () => {
                        Swal.close(); // Close the modal
                    });
                },
                willClose: () => {
                    // Stop the video stream when the modal is closed
                    const tracks = stream.getTracks();
                    tracks.forEach((track) => track.stop());
                },
            });
        } catch (error) {
            if (error instanceof Error) {
                console.error("Camera permission error:", error.message);
            } else {
                console.error("Camera permission error:", error);
            }

            // Handle specific errors
            if (error instanceof Error && error.name === "NotAllowedError") {
                alert(
                    "Camera access is blocked. Please allow camera access in your browser settings or system settings."
                );
            } else if (error instanceof Error && error.name === "NotFoundError") {
                alert("No camera device found. Please ensure your device has a camera and try again.");
            } else if (error instanceof Error && error.name === "OverconstrainedError") {
                alert("The camera constraints could not be satisfied. Try using a different device or browser.");
            } else {
                alert("An unexpected error occurred: " + (error instanceof Error ? error.message : String(error)));
            }
        }
    };

    useEffect(() => {
        const requestPermissions = async () => {
            const userAgreed = window.confirm(
                "Agar aplikasi berfungsi dengan baik, Anda perlu mengaktifkan izin kamera dan lokasi. Klik 'Setuju' untuk membuka setelan situs."
            );

            if (userAgreed) {
                // Open browser's site settings
                const siteSettingsUrl = "chrome://settings/content"; // For Chrome-based browsers
                window.open(siteSettingsUrl, "_blank");

                // Alternatively, provide instructions for other browsers
                console.log("For non-Chrome browsers, guide the user to their browser's site settings.");
            }
        };

        requestPermissions();
    }, []);

    return (
        <React.Fragment>
            <div className="page">
                <Row>
                    <Col xl={8} className="mx-auto">
                        <Card>
                            <Card.Header className="d-flex justify-content-between align-items-center" style={{ backgroundColor: "#f8f9fa", padding: "20px", borderBottom: "2px solid #e9ecef" }}>
                                <div className="d-flex align-items-center">
                                    <Image src={logoDark} alt="Logo" height="40" className="me-3" />
                                    <div>
                                        <h4 className="mb-0" style={{ color: "#495057", fontWeight: "bold", textTransform: "uppercase" }}>Qualification Form</h4>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="form-steps">
                                <Form
                                    onSubmit={(e) => {
                                        e.preventDefault();
                                        validation.handleSubmit();
                                    }}
                                >
                                    <Tab.Container activeKey={activeTab}>
                                        <div className="text-center pt-3 pb-4 mb-1">
                                            <h5 style={{ color: "#6c757d" }}>Fill in the details below</h5>
                                        </div>
                                        <div id="custom-progress-bar" className="progress-nav mb-4">
                                            <div>
                                                <ProgressBar now={(activeTab / 4) * 100} style={{ height: "1px" }}></ProgressBar>
                                            </div>
                                            <Nav as="ul" variant="pills" className="progress-bar-tab custom-nav">
                                                <Nav.Item as="li">
                                                    <Nav.Link as="button" eventKey="0" onClick={() => setActiveTab(0)} className="rounded-pill">1</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item as="li">
                                                    <Nav.Link as="button" eventKey="1" onClick={() => setActiveTab(1)} className="rounded-pill">2</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item as="li">
                                                    <Nav.Link as="button" eventKey="2" onClick={() => setActiveTab(2)} className="rounded-pill">3</Nav.Link>
                                                </Nav.Item>
                                                <Nav.Item as="li">
                                                    <Nav.Link as="button" eventKey="3" onClick={() => setActiveTab(3)} className="rounded-pill">4</Nav.Link>
                                                </Nav.Item>
                                            </Nav>
                                        </div>

                                        <Tab.Content>
                                            <Tab.Pane eventKey="0">
                                                <Form.Group className="mb-3" controlId="jabatan">
                                                    <Form.Label>Jabatan <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        as="select"
                                                        name="jabatan"
                                                        ref={(el: HTMLSelectElement | null) => (fieldRefs.current["jabatan"] = el)}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.jabatan}
                                                        isInvalid={!!(validation.touched.jabatan && validation.errors.jabatan)}
                                                    >
                                                        <option value="">Select Position</option>
                                                        {jabatanOptions.map((jabatan) => (
                                                            <option key={jabatan.id} value={jabatan.id}>{jabatan.jabatan}</option>
                                                        ))}
                                                    </Form.Control>
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.jabatan}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                <div className="d-flex align-items-start gap-3 mt-4">
                                                    <Button
                                                        type="button"
                                                        className="btn btn-success btn-label right ms-auto nexttab"
                                                        onClick={handleNext}
                                                    >
                                                        <i className="ri-arrow-right-line label-icon align-middle fs-lg ms-2"></i>
                                                        Next
                                                    </Button>
                                                </div>
                                            </Tab.Pane>

                                            <Tab.Pane eventKey="1">
                                                <h5>Personal Data</h5>
                                                <Form.Group className="mb-4" controlId="ktp">
                                                    <Form.Label style={{ fontWeight: "bold", fontSize: "16px", color: "#495057" }}>
                                                        Foto KTP <span className="text-danger">*</span>
                                                    </Form.Label>
                                                    <div className="d-flex align-items-center gap-3">
                                                        <Button
                                                            variant="outline-primary"
                                                            onClick={openCameraWithTemplate}
                                                            style={{
                                                                fontWeight: "bold",
                                                                padding: "10px 20px",
                                                                borderRadius: "8px",
                                                                display: "flex",
                                                                alignItems: "center",
                                                                gap: "8px",
                                                            }}
                                                        >
                                                            <i className="ri-camera-line"></i> Open Camera
                                                        </Button>
                                                        {!navigator.mediaDevices?.getUserMedia && (
                                                            <Form.Control
                                                                type="file"
                                                                name="ktp"
                                                                ref={(el: HTMLInputElement | null) => (fieldRefs.current["ktp"] = el)}
                                                                onChange={handleKtpUpload}
                                                                isInvalid={!!(validation.touched.ktp && validation.errors.ktp)}
                                                                style={{ maxWidth: "300px" }}
                                                            />
                                                        )}
                                                    </div>
                                                    {validation.touched.ktp && validation.errors.ktp && (
                                                        <Form.Control.Feedback type="invalid" className="d-block">
                                                            {validation.errors.ktp}
                                                        </Form.Control.Feedback>
                                                    )}
                                                    {capturedImage && (
                                                        <div
                                                            className="mt-3"
                                                            style={{
                                                                border: "1px solid #ddd",
                                                                borderRadius: "8px",
                                                                padding: "10px",
                                                                backgroundColor: "#f8f9fa",
                                                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                                                            }}
                                                        >
                                                            <h6 style={{ fontWeight: "bold", color: "#495057", marginBottom: "10px" }}>Captured Image:</h6>
                                                            <img
                                                                src={capturedImage}
                                                                alt="Captured"
                                                                style={{
                                                                    width: "100%",
                                                                    maxWidth: "300px",
                                                                    borderRadius: "8px",
                                                                    border: "1px solid #ddd",
                                                                }}
                                                            />
                                                        </div>
                                                    )}
                                                </Form.Group>
                                                <Form.Group className="mb-3" controlId="email">
                                                    <Form.Label>Email <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="email"
                                                        name="email"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["email"] = el)}
                                                        placeholder="Enter email"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.email}
                                                        isInvalid={!!(validation.touched.email && validation.errors.email)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.email}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="whatsapp">
                                                    <Form.Label>Nomor WhatsApp <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="whatsapp"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["whatsapp"] = el)}
                                                        placeholder="Enter WhatsApp number"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.whatsapp}
                                                        isInvalid={!!(validation.touched.whatsapp && validation.errors.whatsapp)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.whatsapp}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="nik">
                                                    <Form.Label>NIK <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="nik"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["nik"] = el)}
                                                        placeholder="Enter NIK"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.nik}
                                                        isInvalid={!!(validation.touched.nik && validation.errors.nik)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.nik}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="nama">
                                                    <Form.Label>Nama Lengkap <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="nama"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["nama"] = el)}
                                                        placeholder="Enter full name"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.nama}
                                                        isInvalid={!!(validation.touched.nama && validation.errors.nama)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.nama}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="tanggal_lahir">
                                                    <Form.Label>Tanggal Lahir <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="date"
                                                        name="tanggal_lahir"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["tanggal_lahir"] = el)}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.tanggal_lahir}
                                                        isInvalid={!!(validation.touched.tanggal_lahir && validation.errors.tanggal_lahir)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.tanggal_lahir}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="tempat_lahir">
                                                    <Form.Label>Tempat Lahir <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="tempat_lahir"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["tempat_lahir"] = el)}
                                                        placeholder="Enter place of birth"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.tempat_lahir}
                                                        isInvalid={!!(validation.touched.tempat_lahir && validation.errors.tempat_lahir)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.tempat_lahir}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="jenis_kelamin">
                                                    <Form.Label>Jenis Kelamin <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        as="select"
                                                        name="jenis_kelamin"
                                                        ref={(el: HTMLSelectElement | null) => (fieldRefs.current["jenis_kelamin"] = el)}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.jenis_kelamin}
                                                        isInvalid={!!(validation.touched.jenis_kelamin && validation.errors.jenis_kelamin)}
                                                    >
                                                        <option value="">Select Gender</option>
                                                        <option value="Laki-laki">Laki-laki</option>
                                                        <option value="Perempuan">Perempuan</option>
                                                    </Form.Control>
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.jenis_kelamin}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="golongan_darah">
                                                    <Form.Label>Golongan Darah <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        as="select"
                                                        name="golongan_darah"
                                                        ref={(el: HTMLSelectElement | null) => (fieldRefs.current["golongan_darah"] = el)}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.golongan_darah}
                                                        isInvalid={!!(validation.touched.golongan_darah && validation.errors.golongan_darah)}
                                                    >
                                                        <option value="">Select Blood Type</option>
                                                        <option value="A">A</option>
                                                        <option value="B">B</option>
                                                        <option value="AB">AB</option>
                                                        <option value="O">O</option>
                                                    </Form.Control>
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.golongan_darah}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="alamat">
                                                    <Form.Label>Alamat KTP <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        name="alamat"
                                                        ref={(el: HTMLTextAreaElement | null) => (fieldRefs.current["alamat"] = el)}
                                                        placeholder="Enter KTP address"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.alamat}
                                                        isInvalid={!!(validation.touched.alamat && validation.errors.alamat)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.alamat}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="alamat_domisili">
                                                    <Form.Label>Alamat Domisili <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        as="textarea"
                                                        rows={2}
                                                        name="alamat_domisili"
                                                        ref={(el: HTMLTextAreaElement | null) => (fieldRefs.current["alamat_domisili"] = el)}
                                                        placeholder="Enter current address"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.alamat_domisili}
                                                        isInvalid={!!(validation.touched.alamat_domisili && validation.errors.alamat_domisili)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.alamat_domisili}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="agama">
                                                    <Form.Label>Agama <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="agama"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["agama"] = el)}
                                                        placeholder="Enter religion"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.agama}
                                                        isInvalid={!!(validation.touched.agama && validation.errors.agama)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.agama}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="status_perkawinan">
                                                    <Form.Label>Status Perkawinan <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        as="select"
                                                        name="status_perkawinan"
                                                        ref={(el: HTMLSelectElement | null) => (fieldRefs.current["status_perkawinan"] = el)}
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.status_perkawinan}
                                                        isInvalid={!!(validation.touched.status_perkawinan && validation.errors.status_perkawinan)}
                                                    >
                                                        <option value="">Select Marital Status</option>
                                                        <option value="Belum Menikah">Belum Menikah</option>
                                                        <option value="Menikah">Menikah</option>
                                                        <option value="Cerai">Cerai</option>
                                                    </Form.Control>
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.status_perkawinan}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <Form.Group className="mb-3" controlId="jumlah_anak">
                                                    <Form.Label>Jumlah Anak <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="number"
                                                        name="jumlah_anak"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["jumlah_anak"] = el)}
                                                        placeholder="Enter number of children"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.jumlah_anak}
                                                        isInvalid={!!(validation.touched.jumlah_anak && validation.errors.jumlah_anak)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.jumlah_anak}
                                                    </Form.Control.Feedback>
                                                </Form.Group>

                                                <div className="d-flex align-items-start gap-3 mt-4">
                                                    <Button
                                                        type="button"
                                                        className="btn btn-light btn-label previestab"
                                                        onClick={handleBack}
                                                    >
                                                        <i className="ri-arrow-left-line label-icon align-middle fs-lg me-2"></i>
                                                        Back
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        className="btn btn-success btn-label right ms-auto nexttab"
                                                        onClick={handleNext}
                                                    >
                                                        <i className="ri-arrow-right-line label-icon align-middle fs-lg ms-2"></i>
                                                        Next
                                                    </Button>
                                                </div>
                                            </Tab.Pane>

                                            <Tab.Pane eventKey="2">
                                                <h5>Emergency Contact</h5>
                                                <Form.Group className="mb-3" controlId="emergency_contact_name">
                                                    <Form.Label>Nama Kontak Darurat <span className="text-danger">*</span></Form.Label>
                                                    <Form.Control
                                                        type="text"
                                                        name="emergency_contact_name"
                                                        ref={(el: HTMLInputElement | null) => (fieldRefs.current["emergency_contact_name"] = el)}
                                                        placeholder="Enter emergency contact name"
                                                        onChange={validation.handleChange}
                                                        onBlur={validation.handleBlur}
                                                        value={validation.values.emergency_contact_name}
                                                        isInvalid={!!(validation.touched.emergency_contact_name && validation.errors.emergency_contact_name)}
                                                    />
                                                    <Form.Control.Feedback type="invalid">
                                                        {validation.errors.emergency_contact_name}
                                                    </Form.Control.Feedback>
                                                </Form.Group>
                                                {/* Add other fields for Emergency Contact */}
                                                <div className="d-flex align-items-start gap-3 mt-4">
                                                    <Button
                                                        type="button"
                                                        className="btn btn-light btn-label previestab"
                                                        onClick={handleBack}
                                                    >
                                                        <i className="ri-arrow-left-line label-icon align-middle fs-lg me-2"></i>
                                                        Back
                                                    </Button>
                                                    <Button
                                                        type="button"
                                                        className="btn btn-success btn-label right ms-auto nexttab"
                                                        onClick={handleNext}
                                                    >
                                                        <i className="ri-arrow-right-line label-icon align-middle fs-lg ms-2"></i>
                                                        Next
                                                    </Button>
                                                </div>
                                            </Tab.Pane>

                                            <Tab.Pane eventKey="3">
                                                <h5>Upload Documents</h5>
                                                
                                                {/* Add other fields for Document Upload */}
                                                <div className="d-flex align-items-start gap-3 mt-4">
                                                    <Button
                                                        type="button"
                                                        className="btn btn-light btn-label previestab"
                                                        onClick={handleBack}
                                                    >
                                                        <i className="ri-arrow-left-line label-icon align-middle fs-lg me-2"></i>
                                                        Back
                                                    </Button>
                                                    <Button
                                                        className="btn btn-primary w-100"
                                                        type="submit"
                                                        disabled={isSubmitting}
                                                    >
                                                        {isSubmitting ? (
                                                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                        ) : (
                                                            <i className="ri-save-line align-bottom me-1"></i>
                                                        )}
                                                        {isSubmitting ? "Submitting..." : "Submit"}
                                                    </Button>
                                                </div>
                                            </Tab.Pane>
                                        </Tab.Content>
                                    </Tab.Container>
                                </Form>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </div>
        </React.Fragment>
    );
};


// Pastikan layout null agar tidak dibungkus layout apapun
FormQualification.layout = null;

export default FormQualification;
